import { Component, OnInit, OnDestroy, AfterViewInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProductService } from '../../services/product.service';
import { Product, ProductCategory } from '../../models/product.model';

/** Products per page = 4 cols × 3 rows minimum */
const PAGE_SIZE = 12;

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.scss'
})
export class ProductListComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  @ViewChild('scrollSentinel') scrollSentinel!: ElementRef<HTMLElement>;

  products: Product[] = [];
  categories: ProductCategory[] = ['Breads', 'Pastries', 'Cakes', 'Drinks'];
  selectedCategory: ProductCategory | 'All' = 'All';
  isLoading = true;
  isLoadingMore = false;
  hasMore = true;

  private routeSub!: Subscription;
  private observer: IntersectionObserver | null = null;

  constructor(
    private productService: ProductService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.routeSub = this.route.paramMap.subscribe(params => {
      const cat = params.get('category');
      if (cat) {
        const normalised = cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
        this.selectedCategory = this.categories.includes(normalised as ProductCategory)
          ? (normalised as ProductCategory)
          : 'All';
      } else {
        this.selectedCategory = 'All';
      }
      this.loadProducts();
    });
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
  }

  ngAfterViewChecked() {
    this.observeSentinel();
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.observer?.disconnect();
  }

  private observerReady = false;
  private setupIntersectionObserver() {
    if (this.observer) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && this.hasMore && !this.isLoading && !this.isLoadingMore) {
          this.loadMore();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );
  }

  private observeSentinel() {
    if (!this.observer || !this.hasMore || this.isLoading) return;
    const el = this.scrollSentinel?.nativeElement;
    if (el && !this.observerReady) {
      this.observer.observe(el);
      this.observerReady = true;
    } else if (!el && this.observerReady) {
      this.observerReady = false;
    }
  }

  loadProducts() {
    this.isLoading = true;
    this.products = [];
    this.hasMore = true;
    this.loadPage(0);
  }

  private loadPage(offset: number, append = false) {
    const isInitial = offset === 0;
    if (isInitial) this.isLoading = true; else this.isLoadingMore = true;

    const next = (data: { products: Product[]; hasMore: boolean }) => {
      if (append) {
        this.products = [...this.products, ...data.products];
      } else {
        this.products = data.products;
      }
      this.hasMore = data.hasMore;
      this.isLoading = false;
      this.isLoadingMore = false;
    };

    if (this.selectedCategory === 'All') {
      this.productService.getProductsPaginated(PAGE_SIZE, offset).subscribe(next);
    } else {
      this.productService.getProductsByCategoryPaginated(this.selectedCategory, PAGE_SIZE, offset).subscribe(next);
    }
  }

  loadMore() {
    if (!this.hasMore || this.isLoadingMore || this.isLoading) return;
    this.loadPage(this.products.length, true);
  }

  selectCategory(category: ProductCategory | 'All') {
    if (this.selectedCategory === category) return;
    if (category === 'All') {
      this.router.navigate(['/products']);
    } else {
      this.router.navigate(['/products', category.toLowerCase()]);
    }
  }

  getImgSrc(url: string | undefined): string {
    if (!url) return '';
    return url.startsWith('/uploads/') ? `https://dev.thehuskbakery.com/${url}` : url;
  }
}
